import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { navGroups, type NavItem } from "./nav-config";
import { activeSettingsGroup, settingsGroups, visibleSettingsItems } from "./settings-nav";
import { usePlanScreens } from "../screens/usePlanScreens";
import { clinicFeaturesApi, clinicFeaturesQueryKeys } from "../api/clinicFeatures";
import { meApi } from "../api/me";
import {
  operatingModeApi,
  operatingModeQueryKeys,
  type OperatingModule,
} from "../api/operatingMode";

const FOLD_KEY = "addy.nav.folded";

export function Sidebar({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const location = useLocation();
  // Which settings category (if any) the current route belongs to — used to
  // keep the right "Cài đặt" sub-item highlighted across all its pages.
  const activeSettings = activeSettingsGroup(location.pathname)?.label;

  // Clinic feature flags (ADR-0027) gate the prescription/inventory nav items.
  // Show by default until loaded / on error; hide only when a flag is explicitly off.
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  // Permissions decide which menus are offered (#18746). While this is loading nothing
  // permission-gated is shown: flashing a menu and taking it away reads as a glitch, and the
  // endpoint would refuse the click anyway.
  const profile = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });

  // Chế độ vận hành (ADR-0048): a clinic whose system of record is an external HIS does not run
  // the clinical, pharmacy, billing, vaccination or reporting areas here. Read from the server
  // rather than derived from the mode locally — the same list drives the 409 on the endpoint, and
  // two copies of one rule drift.
  const operating = useQuery({
    queryKey: operatingModeQueryKeys.current(),
    queryFn: () => operatingModeApi.get(),
    staleTime: 5 * 60_000,
  });

  // Gói của phòng khám (ADR-0053). Chưa có câu trả lời thì chưa vẽ mục nào: hiện cả menu rồi rút
  // bớt đi trông như lỗi, cùng luật với quyền và chế độ vận hành bên dưới.
  const plan = usePlanScreens();
  const permissions = profile.data?.permissions ?? [];

  const itemVisible = (item: {
    requiresFeature?: "prescribeEnabled" | "manageInventory";
    requiresPermission?: string | readonly string[];
    requiresModule?: OperatingModule;
  }) => {
    const feat = item.requiresFeature;
    if (feat && features.data && features.data[feat] === false) return false;
    // Same rule as the permission check below: while it loads, hide. Showing "Hoá đơn" and taking
    // it away a moment later reads as a glitch, and the endpoint refuses the click anyway.
    if (item.requiresModule) {
      if (!operating.data) return false;
      if (!operating.data.modules.includes(item.requiresModule)) return false;
    }
    if (item.requiresPermission) {
      const need = item.requiresPermission;
      return typeof need === "string"
        ? permissions.includes(need)
        : need.some((p) => permissions.includes(p));
    }
    return true;
  };

  /**
   * Mục "Cài đặt › <nhóm>" dẫn tới mục đầu tiên người này THẤY ĐƯỢC của nhóm, không phải mục đầu
   * tiên của nhóm: gói có thể không có mục đầu, và dẫn vào đó là dẫn vào trang báo "không có trong
   * gói". Nhóm không còn mục nào thì trả undefined và mục bị ẩn.
   */
  const settingsLanding = (groupLabel: string): string | undefined => {
    const group = settingsGroups.find((g) => g.label === groupLabel);
    return group && visibleSettingsItems(group, { permissions, allowsPath: plan.allowsPath })[0]?.path;
  };

  /** Mục menu như sẽ được vẽ, hoặc null khi người này không được thấy nó. */
  const resolve = (item: NavItem): NavItem | null => {
    if (!plan.settled) return null;
    if (item.settingsGroup) {
      const landing = settingsLanding(item.settingsGroup);
      return landing ? { ...item, path: landing } : null;
    }
    return plan.allowsPath(item.path) && itemVisible(item) ? item : null;
  };

  const [folded, setFolded] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(FOLD_KEY);
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return Object.fromEntries(
      navGroups.filter((g) => g.defaultCollapsed).map((g) => [g.label, true])
    );
  });

  function toggle(label: string) {
    setFolded((f) => {
      const next = { ...f, [label]: !f[label] };
      try {
        localStorage.setItem(FOLD_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <nav className="space-y-1">
          {navGroups.map((group) => {
            const visibleItems = group.items
              .map(resolve)
              .filter((item): item is NavItem => item !== null);
            if (visibleItems.length === 0) return null;
            // Keep the "Cài đặt" group open whenever we're on one of its pages,
            // so the active category stays visible regardless of the fold state.
            const hasActiveSettings = group.items.some(
              (i) => i.settingsGroup && i.settingsGroup === activeSettings
            );
            const isFolded = !collapsed && !!folded[group.label] && !hasActiveSettings;
            return (
              <div key={group.label}>
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => toggle(group.label)}
                    className="flex w-full items-center justify-between rounded px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 transition-colors hover:text-foreground"
                  >
                    <span className="truncate">{group.label}</span>
                    {isFolded ? (
                      <ChevronRight size={14} className="shrink-0" />
                    ) : (
                      <ChevronDown size={14} className="shrink-0" />
                    )}
                  </button>
                )}
                {!isFolded && (
                  <ul className="space-y-0.5">
                    {visibleItems.map((item) => {
                      // Settings-category items highlight for the whole category;
                      // regular items fall back to the router's exact-path match.
                      const forcedActive = item.settingsGroup
                        ? activeSettings === item.settingsGroup
                        : undefined;
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            onClick={onNavigate}
                            title={item.label}
                            className={({ isActive: navActive }) => {
                              const isActive = forcedActive ?? navActive;
                              return [
                                "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                                collapsed ? "justify-center" : "",
                                isActive
                                  ? "bg-muted font-medium text-foreground"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
                              ].join(" ");
                            }}
                          >
                            {({ isActive: navActive }) => {
                              const isActive = forcedActive ?? navActive;
                              return (
                                <>
                                  <item.icon
                                    size={18}
                                    strokeWidth={2}
                                    className={isActive ? "shrink-0 text-primary" : "shrink-0"}
                                  />
                                  {!collapsed && <span className="truncate">{item.label}</span>}
                                </>
                              );
                            }}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Help — pinned below the configuration nav. */}
      <div className="border-t px-2 py-2">
        <NavLink
          to="/help"
          onClick={onNavigate}
          title="Trợ giúp"
          className={({ isActive }) =>
            [
              "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
              collapsed ? "justify-center" : "",
              isActive
                ? "bg-muted font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              <HelpCircle
                size={18}
                strokeWidth={2}
                className={isActive ? "shrink-0 text-primary" : "shrink-0"}
              />
              {!collapsed && <span className="truncate">Trợ giúp</span>}
            </>
          )}
        </NavLink>
      </div>
    </>
  );
}
